import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { AutoComplete } from 'antd';
import api from '../../api/client';

// Одно поле-автодополнение «Автомобиль» (vehicle_resolver.md §5 rev. №2):
// ввод любого текста → подсказки «Марка Модель» из справочника AR_DB.
// Выбор опции → make+model раздельно; свободный текст → только model (custom).

export interface VehiclePick {
  make: string | null;
  model: string | null;
}

interface ModelRow {
  id: number;
  make: string;
  model: string;
  make_ru: string | null;
  model_ru: string | null;
}

interface Props {
  make: string | null;
  model: string | null;
  // Имя авто из 1С у существующего заказа (марка/модель не выбраны)
  legacyName?: string | null;
  disabled?: boolean;
  autoFocus?: boolean;
  onChange: (v: VehiclePick) => void;
}

const displayOf = (make: string | null, model: string | null, legacyName?: string | null) => {
  if (make && model) return `${make} ${model}`;
  if (model) return model;
  if (legacyName) return legacyName;
  return '';
};

export interface VehiclePickerRef {
  focus: () => void;
}

const VehiclePicker = forwardRef<VehiclePickerRef, Props>(({ make, model, legacyName, disabled, autoFocus, onChange }, ref) => {
  const [text, setText] = useState(() => displayOf(make, model, legacyName));
  const [options, setOptions] = useState<{ value: string; label: React.ReactNode; make: string; model: string }[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const skipChange = useRef(false);
  const acRef = useRef<any>(null);

  useImperativeHandle(ref, () => ({ focus: () => acRef.current?.focus?.() }), []);

  // Синхронизация с внешним значением (загрузка существующего заказа)
  useEffect(() => {
    setText(displayOf(make, model, legacyName));
  }, [make, model, legacyName]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const handleSearch = (q: string) => {
    clearTimeout(timer.current);
    if (!q.trim()) { setOptions([]); return; }
    timer.current = setTimeout(() => {
      api.get('/vehicle-models', { params: { q, limit: 20 } })
        .then(r => setOptions((r.data as ModelRow[]).map(row => {
          const title = `${row.make} ${row.model}`;
          const ru = [row.make_ru, row.model_ru].filter(Boolean).join(' ');
          return {
            value: `${row.id}`,
            make: row.make,
            model: row.model,
            label: (
              <span>
                {title}
                {ru && ru.toLowerCase() !== title.toLowerCase() && (
                  <span style={{ color: '#999' }}> — {ru}</span>
                )}
              </span>
            ),
          };
        })))
        .catch(() => setOptions([]));
    }, 300);
  };

  return (
    <AutoComplete
      ref={acRef}
      style={{ width: '100%' }}
      value={text}
      options={options}
      placeholder="Марка и модель (например: Дастер, Focus)"
      autoFocus={autoFocus}
      disabled={disabled}
      allowClear
      onSearch={handleSearch}
      onSelect={(_, option) => {
        skipChange.current = true;
        setText(`${option.make} ${option.model}`);
        setOptions([]);
        onChange({ make: option.make, model: option.model });
      }}
      onChange={v => {
        if (skipChange.current) { skipChange.current = false; return; }
        setText(v);
        onChange({ make: null, model: v || null });
      }}
      notFoundContent="Не найдено — текст уйдёт как есть"
    />
  );
});

VehiclePicker.displayName = 'VehiclePicker';

export default VehiclePicker;
